import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { Menu } from '../constants/menu.constant';
import { MenuModel } from '../models/menu.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';

const TOKEN_KEY = 'auth-token';
const MENU_KEY = 'menu';
const PERMISSION_KEY = 'permissions';
const TOTAL_ROWS_KEY = 'totalRows';
const COMPLAINT_LIST_STATE_KEY = 'complaint_list_state';
@Injectable({
    providedIn: 'root'
})
export class TokenStorageService {

    validFrames: any = [];

    constructor(private router: Router, private jwtHelper: JwtHelperService, private cookieService: CookieService) { }

    public saveToken(token: string) {
        const tokenPayload = this.decodeToken();

        localStorage.removeItem(TOKEN_KEY);
        localStorage.setItem(TOKEN_KEY, token);
    }

    public savePermission(tokenPayload: string) {
        localStorage.setItem(PERMISSION_KEY, JSON.stringify(tokenPayload));
    }
    public getToken(): any {
        return (localStorage.getItem(TOKEN_KEY)) ? localStorage.getItem(TOKEN_KEY) : false;
    }
    public removeToken(): any {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(MENU_KEY);
        localStorage.removeItem(PERMISSION_KEY);
        localStorage.removeItem(COMPLAINT_LIST_STATE_KEY);
        localStorage.removeItem(TOTAL_ROWS_KEY);
    }
    public isAuthenticated(): boolean {
        if (this.getToken()) {
            return !this.jwtHelper.isTokenExpired(this.getToken());
        }
        return false;
    }

    public decodeToken(): any {
        if (this.getToken()) {
            return jwtDecode<any>(this.getToken());
        }
        return null;
    }

    public setPermissions(): void {
        this.validFrames = [];
        const tokenPayload = this.decodeToken();
        const userFrames: string[] = tokenPayload?.FRAMES ?? [];
        // Iterate Menu array (not JWT frames) so array order in menu.constant.ts drives display order
        Menu.forEach(m => {
            if (userFrames.includes(m.frame)) {
                this.addValidFrame(m);
            } else if (m.subMenu && m.subMenu.length > 0) {
                const hasSubFrame = m.subMenu.some((sub: MenuModel) => userFrames.includes(sub.frame));
                if (hasSubFrame) {
                    this.addValidFrame(m);
                }
            }
        });
        localStorage.setItem(MENU_KEY, JSON.stringify(this.validFrames));

        const frames: string[] = userFrames;
        let landingRoute = 'dashboard/gender';
        if (frames.includes('SUPERVISOR') && !frames.includes('DASHBOARD_ONE')) {
            landingRoute = '/telephony/supervisor/control-room';
        } else if (frames.includes('CSR_CONSOLE') && !frames.includes('DASHBOARD_ONE')) {
            landingRoute = '/telephony';
        }
        this.router.navigate([landingRoute]);

    }
    private addValidFrame(menu: MenuModel): void {
        const exists = this.validFrames.some((m: MenuModel) => m.title === menu.title && m.route === menu.route);
        if (!exists) {
            this.validFrames.push(menu);
        }
    }
    public getPermission(): any {
        return (localStorage.getItem(PERMISSION_KEY)) ? localStorage.getItem(PERMISSION_KEY) : null;

    }

    public getValidFrames(): any {
        let menu: any = [];
        menu = localStorage.getItem(MENU_KEY);
        if (menu) {
            return this.syncStoredMenuWithCurrentDefinition(JSON.parse(menu));
        }
        return menu;
    }

    private syncStoredMenuWithCurrentDefinition(storedMenu: MenuModel[]): MenuModel[] {
        const tokenPayload = this.decodeToken();
        const userFrames: string[] = tokenPayload?.FRAMES ?? [];
        if (!Array.isArray(storedMenu) || userFrames.length === 0) {
            return storedMenu;
        }

        const refreshed: MenuModel[] = [];
        Menu.forEach(currentMenu => {
            const parentAllowed = userFrames.includes(currentMenu.frame);
            const allowedSubMenu = (currentMenu.subMenu || []).filter(sub => userFrames.includes(sub.frame));

            if (parentAllowed || allowedSubMenu.length > 0) {
                refreshed.push({
                    ...currentMenu,
                    subMenu: currentMenu.subMenu
                });
            }
        });

        localStorage.setItem(MENU_KEY, JSON.stringify(refreshed));
        return refreshed;
    }
    public getWorkItemSteps(): any {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.PROCESS;
        }
        return [];
    }
    public getDivisions(): any {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.DIVISION;
        }
        return [];
    }
    public getDistricts(): any {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.DISTRICT;
        }
        return [];
    }
    public getTehsil(): any {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.TEHSIL;
        }
        return [];
    }
    public getMouza(): any {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.MOUZA;
        }
        return [];
    }
    public getUserId(): any {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.USER;
        }
        return null;
    }
    public getSegmentId(): any {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.SEGMENT;
        }
        return null;
    }
    public getEmail() {
        const tokenPayload = this.decodeToken();
        if (tokenPayload != null) {
            return tokenPayload?.sub;
        }
        return '';
    }

    public getCanseeInfo() {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.canSeeInfo ?? false;
        }
        return false;
    }
    public getCanViewAnonymousInfo() {
        let item = this.getPermission();
        if (item != null) {
            return JSON.parse(item)?.canViewAnonymousInfo ?? false;
        }
        return false;
    }
}
